import {
  Box,
  Button,
  ButtonProps,
  Divider,
  styled,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { navigate } from 'gatsby'
import React from 'react'
import { colors } from '../../../constants/colors'
import { HBox } from '../../../elements/basic-elements'
import CheckmarkIcon from '../../../images/icons/checkmark-icon'
import { OnboardingStepState, OnboardingStepToUrl } from '../../../types/onboarding-types'
import { excludeScProps } from '../../../utilities/browser'
import { capitalizeFirstLetter } from '../../../utilities/string'
import { HEADER_ONBOARDING_STEPS, ONBOARDING_HEADER_MOBILE_BREAKPOINT } from './header-onboarding'

interface HeaderOnboardingStepProps {
  stepState: OnboardingStepState
  stepNumber: number
  stepLabel: string
  disabled: boolean
}

const HeaderOnboardingStep = ({
  stepState,
  stepNumber,
  stepLabel,
  disabled
}: HeaderOnboardingStepProps) => {
  const theme = useTheme()

  const compactNavbarBreakpoint = useMediaQuery(
    theme.breakpoints.down(ONBOARDING_HEADER_MOBILE_BREAKPOINT)
  )

  const onClick = () => navigate(OnboardingStepToUrl[HEADER_ONBOARDING_STEPS[stepNumber]])

  const stepProps = { disabled, onClick }

  if (compactNavbarBreakpoint) {
    return stepState === OnboardingStepState.IN_PROGRESS ? (
      <Step stepState={stepState} stepNumber={stepNumber} {...stepProps}>
        <Typography variant="subtitle2">{capitalizeFirstLetter(stepLabel)}</Typography>
      </Step>
    ) : (
      <StepperDot filled={stepState === OnboardingStepState.COMPLETED} />
    )
  }

  return (
    <HBox alignItems="center">
      <Step stepState={stepState} stepNumber={stepNumber} {...stepProps}>
        {stepState === OnboardingStepState.COMPLETED && <CheckmarkIcon size={14} />}
        <Typography variant="subtitle2">{capitalizeFirstLetter(stepLabel)}</Typography>
      </Step>
      {stepNumber !== HEADER_ONBOARDING_STEPS.length - 1 && <StepDivider />}
    </HBox>
  )
}

const COLOR_MAPPING = {
  [OnboardingStepState.NOT_STARTED]: colors.GEORGIAN_BAY_100,
  [OnboardingStepState.STARTED]: colors.GEORGIAN_BAY_100,
  [OnboardingStepState.IN_PROGRESS]: colors.WHITE,
  [OnboardingStepState.COMPLETED]: colors.GEORGIAN_BAY_100
}

const DARK_MODE_COLOR_MAPPING = {
  [OnboardingStepState.NOT_STARTED]: colors.DARK_MODE_30,
  [OnboardingStepState.STARTED]: colors.WHITE,
  [OnboardingStepState.IN_PROGRESS]: colors.WHITE,
  [OnboardingStepState.COMPLETED]: colors.WHITE
}

const Step = styled(
  (props: ButtonProps) => <Button disableRipple variant="text" {...props} />,
  excludeScProps(['stepNumber', 'stepState'])
)<{
  stepState: OnboardingStepState
  stepNumber: number
}>`
  align-items: center;
  gap: 3px;
  ${({ stepState, stepNumber, theme }) => `
        background-color: ${stepState === OnboardingStepState.IN_PROGRESS ? colors.RED_100 : 'unset'};
        color: ${theme.palette.mode === 'dark' ? DARK_MODE_COLOR_MAPPING[stepState] : COLOR_MAPPING[stepState]};
        border-radius: 10px;
        padding: ${stepState === OnboardingStepState.IN_PROGRESS ? '5px 10px' : '0px 8px'};
        margin-left: ${stepState === OnboardingStepState.IN_PROGRESS && stepNumber !== 0 ? '8px' : 0};
        margin-right: ${stepState === OnboardingStepState.IN_PROGRESS && stepNumber !== HEADER_ONBOARDING_STEPS.length - 1 ? '8px' : 0};
        ${theme.breakpoints.down('md')} {
            margin: 0px 2px;
        }
        &:hover {
            ${
              stepState === OnboardingStepState.IN_PROGRESS
                ? `background-color: ${colors.RED_100}};`
                : `
                  text-decoration: underline;
                  background-color: transparent;
                `
            }
        }
        &.MuiButtonBase-root.Mui-disabled {
            color: ${theme.palette.mode === 'dark' ? DARK_MODE_COLOR_MAPPING[stepState] : COLOR_MAPPING[stepState]};
        }
  `}
`
const StepDivider = styled(Divider)`
  ${({ theme }) => `
    width: ${theme.spacing(2)};
    background-color: ${theme.palette.mode === 'dark' ? colors.DARK_MODE_10 : colors.GEORGIAN_BAY_70}
  `}
`

const StepperDot = styled(Box)<{ filled: boolean }>`
  ${({ filled }) => `
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${filled ? colors.GEORGIAN_BAY_100 : 'unset'};
        border: ${filled ? 'unset' : `1px solid ${colors.GEORGIAN_BAY_70}`};
`}
`

export default HeaderOnboardingStep
